import React from 'react'
import {
  Button,
  LinkButton,
  Skeleton,
  useForTablet,
  useModalContext,
} from '@sketch/components'
import { routes } from '@sketch/modules-common'

import { HiddenProfileBanner } from '../HiddenProfileBanner'
import { EditingProfileNavBar } from '../EditingProfileNavBar'
import { ProfileModal, PublishProfileModal } from '../../modals'
import {
  Wrapper,
  Actions,
  ArrowTopRightIcon,
  ProfileStatus,
  StyledText,
  TopBannersSkeleton,
} from './ProfileTopBanners.styles'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { EyeIcon } from '../HiddenProfileBanner/HiddenProfileBanner.styles'
import { GlobeIcon } from '../EditingProfileNavBar/EditingProfileNavBar.styles'

interface ProfileTopBannersProps {
  workspace: WorkspaceMinimalFragment
  profileData: {
    identifier: string
    shortUrlName: string
  }
  isPublic: boolean
  isLoading: boolean
}

export const ProfileTopBanners: React.FC<ProfileTopBannersProps> = ({
  profileData,
  isPublic,
  workspace,
  isLoading,
}) => {
  const { showModal } = useModalContext()
  const isSmallerThanTablet = !useForTablet()

  if (isLoading) {
    return (
      <TopBannersSkeleton>
        <Skeleton height="32px" width="100px" />
        <Skeleton height="32px" width="100px" />
      </TopBannersSkeleton>
    )
  }

  const handlePublishProfile = () => {
    showModal(PublishProfileModal, {
      identifier: profileData.identifier,
    })
  }

  const handleEditProfile = () => {
    showModal(ProfileModal, {
      workspace,
    })
  }

  const renderPrivateActions = () => (
    <>
      <Button
        variant="secondary-untinted"
        size="32"
        onClick={handleEditProfile}
      >
        Edit Profile
      </Button>
      <Button variant="primary" size="32" onClick={handlePublishProfile}>
        Publish
      </Button>
    </>
  )

  const renderPublicActions = () => (
    <>
      <LinkButton
        size="32"
        variant="secondary-untinted"
        href={routes.WORKSPACE_PROFILE.create({
          shortUrlName: profileData.shortUrlName || '',
        })}
        external
      >
        View Profile <ArrowTopRightIcon height="17" width="17" />
      </LinkButton>
      <Button
        variant="secondary-untinted"
        size="32"
        onClick={handleEditProfile}
      >
        Edit Profile
      </Button>
    </>
  )

  const renderMobileBanners = () => (
    <ProfileStatus>
      {isPublic ? (
        <>
          <GlobeIcon /> <StyledText>Public</StyledText>
        </>
      ) : (
        <>
          <EyeIcon /> <StyledText>Hidden</StyledText>
        </>
      )}
    </ProfileStatus>
  )

  const renderActions = () => (
    <Actions>
      {isPublic ? renderPublicActions() : renderPrivateActions()}
    </Actions>
  )

  const renderBanner = () => {
    if (isSmallerThanTablet) {
      return renderMobileBanners()
    }

    return isPublic ? (
      <EditingProfileNavBar />
    ) : (
      <HiddenProfileBanner shortUrlName={profileData.shortUrlName} />
    )
  }

  return (
    <Wrapper>
      {renderBanner()}
      {renderActions()}
    </Wrapper>
  )
}
